import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
import { callApi } from '../../utils/apiCall';

// ─── Initial State ───────────────────────────────────────────────────────────
const initialState = {
    isLoading: false,
    error: null,

    // Survey forms list (form_type = SURVEY)
    surveys: { results: [], count: 0 },
    currentSurvey: null,

    // Submissions for a specific survey form
    submissions: { results: [], count: 0 },
    submissionsLoading: false,
    currentSubmission: null,

    // Survey links (public URLs for anonymous access)
    surveyLinks: { results: [], count: 0 },
    surveyLinksLoading: false,

    // Dashboard stats
    stats: null,
    statsLoading: false,

    // Field-level analytics for a specific survey
    fieldAnalytics: null,
    fieldAnalyticsLoading: false,
};

// ─── Slice ───────────────────────────────────────────────────────────────────
const slice = createSlice({
    name: 'surveyForm',
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },
        stopLoading(state) {
            state.isLoading = false;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },

        // ── Surveys ──────────────────────────────────────────────────────
        setSurveys(state, action) {
            state.isLoading = false;
            state.surveys = action.payload;
        },
        setCurrentSurvey(state, action) {
            state.isLoading = false;
            state.currentSurvey = action.payload;
        },
        resetCurrentSurvey(state) {
            state.currentSurvey = null;
        },

        // ── Submissions ──────────────────────────────────────────────────
        startSubmissionsLoading(state) {
            state.submissionsLoading = true;
        },
        setSubmissions(state, action) {
            state.submissionsLoading = false;
            state.submissions = action.payload;
        },
        setCurrentSubmission(state, action) {
            state.submissionsLoading = false;
            state.currentSubmission = action.payload;
        },
        resetSubmissions(state) {
            state.submissions = { results: [], count: 0 };
            state.currentSubmission = null;
            state.submissionsLoading = false;
        },

        // ── Survey Links ─────────────────────────────────────────────────
        startSurveyLinksLoading(state) {
            state.surveyLinksLoading = true;
        },
        setSurveyLinks(state, action) {
            state.surveyLinksLoading = false;
            state.surveyLinks = action.payload;
        },
        stopSurveyLinksLoading(state) {
            state.surveyLinksLoading = false;
        },

        // ── Stats ────────────────────────────────────────────────────────
        startStatsLoading(state) {
            state.statsLoading = true;
        },
        setStats(state, action) {
            state.statsLoading = false;
            state.stats = action.payload;
        },
        stopStatsLoading(state) {
            state.statsLoading = false;
        },

        // ── Field Analytics ──────────────────────────────────────────────
        startFieldAnalyticsLoading(state) {
            state.fieldAnalyticsLoading = true;
        },
        setFieldAnalytics(state, action) {
            state.fieldAnalyticsLoading = false;
            state.fieldAnalytics = action.payload;
        },
        stopFieldAnalyticsLoading(state) {
            state.fieldAnalyticsLoading = false;
        },
    },
});

export default slice.reducer;

export const {
    resetCurrentSurvey,
    resetSubmissions,
} = slice.actions;

// ═════════════════════════════════════════════════════════════════════════════
// Survey Form CRUD — reuses existing /forms/ endpoint with form_type=SURVEY
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Fetch all survey forms for a practice.
 * Uses existing forms endpoint filtered by form_type=SURVEY.
 */
export function getSurveys(practiceId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('form_type', 'SURVEY');
            searchParams.set('ordering', '-created_at');
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            if (params.search) searchParams.set('search', params.search);
            // Perf: skip the heavy sections/treatments/conditional_logics
            // prefetch graph — the survey list only needs metadata. The BE
            // ``PracticeFormViewSet.get_queryset`` parses ``?fields=`` and
            // skips prefetches whose top-level relation isn't requested
            // (apps/practice/views.py — Track 2026-04-26 perf pass).
            searchParams.set(
                'fields',
                '{id,name,description,form_type,category,is_default,is_template,created_at,created_at_human,updated_at,updated_at_human,created_by}'
            );
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setSurveys(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to load surveys' }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Failed to load surveys' }));
        }
    };
}

/**
 * Fetch a single survey form detail.
 */
export function getSurveyDetail(practiceId, formId) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setCurrentSurvey(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to load survey' }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Failed to load survey' }));
        }
    };
}

/**
 * Create a new survey form.
 * Enforces form_type = 'SURVEY'.
 */
export function createSurvey(practiceId, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const payload = {
                ...data,
                form_type: 'SURVEY',
            };
            if (data.form_mode) payload.form_mode = data.form_mode;
            if (data.step_groups) payload.step_groups = data.step_groups;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/`,
                'post',
                payload
            );
            if (response.status === 201) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to create survey' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Failed to create survey' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Update an existing survey form.
 */
export function updateSurvey(practiceId, formId, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const payload = {
                ...data,
                form_type: 'SURVEY',
            };
            if (data.form_mode) payload.form_mode = data.form_mode;
            if (data.step_groups) payload.step_groups = data.step_groups;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/`,
                'patch',
                payload
            );
            if (response.status === 200) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to update survey' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Failed to update survey' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Delete a survey form.
 */
export function deleteSurvey(practiceId, formId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/`,
                'delete'
            );
            if (response.status === 204) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to delete survey' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Failed to delete survey' }));
            if (callback) callback({ success: false });
        }
    };
}

// ═════════════════════════════════════════════════════════════════════════════
// Survey Submissions — reuses existing /forms/{id}/submissions/ endpoint
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Fetch submissions for a survey form.
 * Submissions may have lead=null (anonymous) with respondent_* fields.
 */
export function getSurveySubmissions(practiceId, formId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startSubmissionsLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('ordering', '-created_at');
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            if (params.search) searchParams.set('search', params.search);
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/submissions/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setSubmissions(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
        }
    };
}

/**
 * Fetch a single submission detail.
 */
export function getSurveySubmissionDetail(practiceId, formId, submissionId) {
    return async () => {
        try {
            dispatch(slice.actions.startSubmissionsLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/submissions/${submissionId}/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setCurrentSubmission(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
        }
    };
}

// ═════════════════════════════════════════════════════════════════════════════
// Survey Links — NEW endpoints for public anonymous survey access
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Fetch all survey links for a practice.
 * Endpoint: GET /api/v1/practices/{practiceId}/survey-links/
 */
export function getSurveyLinks(practiceId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startSurveyLinksLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('ordering', '-created_at');
            if (params.formId) searchParams.set('form', params.formId);
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            const response = await callApi(
                `/api/v1/practices/${practiceId}/survey-links/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setSurveyLinks(response.data));
            } else {
                dispatch(slice.actions.stopSurveyLinksLoading());
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to load survey links' }));
            }
        } catch (error) {
            dispatch(slice.actions.stopSurveyLinksLoading());
            dispatch(updateError({ success: false, message: error.message || 'Failed to load survey links' }));
        }
    };
}

/**
 * Create a new survey link (public URL for anonymous submissions).
 * Endpoint: POST /api/v1/practices/{practiceId}/survey-links/
 *
 * @param {string} practiceId
 * @param {{ form: number, slug?: string, is_active?: boolean, allow_multiple_submissions?: boolean, expires_at?: string, max_submissions?: number }} data
 * @param {Function} callback
 */
export function createSurveyLink(practiceId, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startSurveyLinksLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/survey-links/`,
                'post',
                data
            );
            if (response.status === 201) {
                dispatch(slice.actions.stopSurveyLinksLoading());
                if (callback) callback({ success: true, data: response.data });
                // Refresh links list
                dispatch(getSurveyLinks(practiceId, { formId: data.form }));
            } else {
                dispatch(slice.actions.stopSurveyLinksLoading());
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to create survey link' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.stopSurveyLinksLoading());
            dispatch(updateError({ success: false, message: error.message || 'Failed to create survey link' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Update a survey link (e.g. deactivate, change expiry).
 * Endpoint: PATCH /api/v1/practices/{practiceId}/survey-links/{linkId}/
 */
export function updateSurveyLink(practiceId, linkId, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startSurveyLinksLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/survey-links/${linkId}/`,
                'patch',
                data
            );
            if (response.status === 200) {
                dispatch(slice.actions.stopSurveyLinksLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.stopSurveyLinksLoading());
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to update survey link' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.stopSurveyLinksLoading());
            dispatch(updateError({ success: false, message: error.message || 'Failed to update survey link' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Delete a survey link.
 * Endpoint: DELETE /api/v1/practices/{practiceId}/survey-links/{linkId}/
 */
export function deleteSurveyLink(practiceId, linkId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startSurveyLinksLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/survey-links/${linkId}/`,
                'delete'
            );
            if (response.status === 204) {
                dispatch(slice.actions.stopSurveyLinksLoading());
                if (callback) callback({ success: true });
            } else {
                dispatch(slice.actions.stopSurveyLinksLoading());
                dispatch(updateError({ success: false, message: 'Failed to delete survey link' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(slice.actions.stopSurveyLinksLoading());
            dispatch(updateError({ success: false, message: error.message || 'Failed to delete survey link' }));
            if (callback) callback({ success: false });
        }
    };
}

// ═════════════════════════════════════════════════════════════════════════════
// Survey Dashboard Stats
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Fetch aggregated survey stats for the dashboard.
 * Endpoint: GET /api/v1/practices/{practiceId}/survey-stats/
 */
export function getSurveyStats(practiceId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startStatsLoading());
            const searchParams = new URLSearchParams();
            if (params.formId) searchParams.set('form_id', params.formId);
            if (params.startDate) searchParams.set('start_date', params.startDate);
            if (params.endDate) searchParams.set('end_date', params.endDate);
            const qs = searchParams.toString();
            const response = await callApi(
                `/api/v1/practices/${practiceId}/survey-stats/${qs ? `?${qs}` : ''}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setStats(response.data));
            } else {
                dispatch(slice.actions.stopStatsLoading());
            }
        } catch {
            dispatch(slice.actions.stopStatsLoading());
        }
    };
}

// ═════════════════════════════════════════════════════════════════════════════
// Survey Field Analytics (Phase 2)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Fetch field-level analytics for a specific survey form.
 * Endpoint: GET /api/v1/practices/{practiceId}/forms/{formId}/analytics/
 */
export function getSurveyFieldAnalytics(practiceId, formId) {
    return async () => {
        try {
            dispatch(slice.actions.startFieldAnalyticsLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/analytics/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setFieldAnalytics(response.data));
            } else {
                dispatch(slice.actions.stopFieldAnalyticsLoading());
            }
        } catch {
            dispatch(slice.actions.stopFieldAnalyticsLoading());
        }
    };
}
